import React, {
  useContext, useState, useEffect, useMemo,
} from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import { useTheme } from 'next-themes';
import { usePlateActions } from '@udecode/plate-core';
import { IPageStoredItem, getOfflinePagesBySpace, removeOfflinePage } from '@typedream/data';
import sharedStyles from '../../styles/Shared.module.css';
import Popup from '../../sharedComponents/TextEditor/components/Popup';
import Button from '../../sharedComponents/Button';
import getDateFormatted from '../../utils/getDate';
import styles from './UnsavedPages.module.css';
import { TextEditorContext } from '../../sharedComponents/TextEditor/context/TextEditorContext';
import { NavbarEditorProvider } from '../../sharedComponents/TextEditor/context/NavbarEditorContextProvider';
import { EditorEditableContext } from '../../sharedComponents/TextEditor/context/EditorEditableContextProvider';
import { initialValueEmpty } from '../../sharedComponents/TextEditor';
import { BUCKET_URL, BUCKET_URL2, EDITOR_API_BASE_URL } from '../../constants';
import { errToString } from '../../utils/errors';
import APIProvider from '../../sharedComponents/TextEditor/context/APIContextProvider';
import { DARK_THEME } from '../../sharedComponents/TextEditor/constants';
import ButtonAccent from '../../sharedComponents/Button/ButtonAccent';
import { useSpaceAndPageIDFromRouter } from '../../data/space/hooks';
import { DreamEditor, DreamValue } from '../../sharedComponents/TextEditor/plate';

const TextEditor = dynamic(() => import('../../sharedComponents/TextEditor'));

function NotPreviewMode({
  unsavedPages, slug, spaceID, pageID, onReview, onDiscard,
}: {
  unsavedPages: IPageStoredItem[]
  slug: string
  spaceID: string
  pageID: string
  onReview(): void
  onDiscard(): void
}) {
  return (
    <>
      <div className="is-subtitle with-light-text-color mb-3">
        You have
        {' '}
        <span className="with-text-color">unsaved changes</span>
        {' '}
        from your previous session.
      </div>
      <hr className={sharedStyles.hr} />

      <div className={sharedStyles.blockItem}>
        {unsavedPages?.map((p) => p.id === pageID && (
        <>
          <div className="is-size-6 with-text-color mb-4">
            Do you want to review your unsaved changes for this page
            {' '}
            <span className="is-subtitle">
              (
              {p.name}
              )
            </span>
            ?
          </div>

          <div className="is-subtitle mb-4">
            Unsaved changes last edited at:
            <br />
            {getDateFormatted(new Date(p.content_updated_at).getTime() / 1000)}
            {' - '}
            {new Date(p.content_updated_at).toLocaleTimeString()}
          </div>
          <div className="is-flex is-flex-direction-row is-align-items-center is-justify-content-flex-start">
            <Button
              onClick={onReview}
              className="p-2"
            >
              Review unsaved changes
            </Button>
            <div
              className="is-subtitle ml-4 has-text-weight-normal has-text-danger"
              style={{ cursor: 'pointer' }}
              onClick={onDiscard}
              role="none"
            >
              Discard draft
            </div>
          </div>
          {unsavedPages?.length > 1 && <hr className={sharedStyles.hr} />}
        </>
        ))}

        {unsavedPages?.length > 1
        && (
          <div className="is-size-6 with-text-color mb-4">
            Review changes on page
            {unsavedPages?.length > 2 && 's'}
            :
          </div>
        )}
        {unsavedPages?.map((p) => p.id !== pageID
        && (
          <div className="is-subtitle-size mb-2">
            <Link href={`/s/${slug}/${spaceID}/${p.id}`} legacyBehavior>
              {`${p.name} →`}
            </Link>
          </div>
        ))}
      </div>
    </>
  );
}

function PreviewMode({
  pageContent, height, width, onRestore, onCancel,
}: {
  pageContent: IPageStoredItem['content']
  height: number
  width: number
  onRestore(): void
  onCancel(): void
}) {
  const { theme } = useTheme();

  return (
    <>
      <div style={{ width: '100%', color: 'var(--text-lighter)' }} className="mb-4 is-flex is-justify-content-space-between is-align-items-center">
        <div>Are you sure you want to restore this page? </div>
        <div className="is-flex">
          <ButtonAccent
            className="mr-2"
            onClick={onRestore}
          >
            Restore
          </ButtonAccent>
          <Button onClick={onCancel}>Cancel</Button>
        </div>
      </div>
      <div
        className={styles.iframeContainer}
        style={{
          backgroundColor: theme === DARK_THEME ? 'var(--background-highlight)' : 'white',
          height: height - 200,
          maxWidth: width - 100,
        }}
      >
        <div className={styles.urlBarContainer}>
          <div className={styles.dotsContainer}>
            <div />
            <div />
            <div />
          </div>
          <div />
          <div />
        </div>
        <div style={{ width: '100%', overflowY: 'scroll' }}>
          <APIProvider
            assetsBaseURL={[BUCKET_URL, BUCKET_URL2]}
            documentBaseURL={EDITOR_API_BASE_URL}
            errToString={errToString}
          >
            <EditorEditableContext.Provider value={false}>
              <NavbarEditorProvider>
                <TextEditorContext.Provider value={useMemo(() => ({
                  initialValue: pageContent,
                  save: () => null,
                  setInitialValue: () => null,
                  isDraft: false,
                  loading: false,
                }), [])}
                >
                  <TextEditor id="unsaved-changes" />
                </TextEditorContext.Provider>
              </NavbarEditorProvider>
            </EditorEditableContext.Provider>
          </APIProvider>
        </div>
      </div>
    </>
  );
}

function UnsavedPagesPopup({
  open,
  setopen,
  unsavedPages,
  setunsavedPages,
}: {
  open: boolean,
  setopen: any,
  unsavedPages: IPageStoredItem[]
  setunsavedPages(value: IPageStoredItem[]): void
}) {
  const { setInitialValue } = useContext(TextEditorContext);
  const setValue = usePlateActions<DreamValue, DreamEditor>().value();
  const router = useRouter();
  const { slug } = router.query;
  const [height, setheight] = useState(350);
  const [width, setWidth] = useState(500);
  const [previewMode, setPreviewMode] = useState(false);

  const [spaceID, pageID] = useSpaceAndPageIDFromRouter();

  const checkUnsavedPages = async () => {
    const spaceUnsavedPages = await getOfflinePagesBySpace(spaceID);
    setunsavedPages(spaceUnsavedPages?.length > 0 ? spaceUnsavedPages : null);
  };

  useEffect(() => {
    setPreviewMode(false);
  }, [open]);

  useEffect(() => {
    if (previewMode) {
      setheight(window.innerHeight - 100);
      setWidth(window.innerWidth - 100);
    } else if (unsavedPages) {
      let baseHeight = 150;
      unsavedPages.forEach((p) => {
        if (p.id === pageID) {
          baseHeight += 200;
        } else {
          baseHeight += 80;
        }
      });
      if (unsavedPages?.length > 0) {
        baseHeight += 80;
      }
      setheight(baseHeight);
      setWidth(500);
    }
  }, [unsavedPages, previewMode]);

  const currentUnsavedPage = useMemo(
    () => unsavedPages?.find((page) => page.id === pageID),
    [unsavedPages],
  );

  const currentUnsavedPageContent = currentUnsavedPage?.content || initialValueEmpty;

  return (
    <Popup
      width={width}
      height={height}
      open={open}
      setopen={setopen}
      className="p-6"
    >
      {previewMode
        ? (
          <PreviewMode
            pageContent={currentUnsavedPageContent}
            height={height}
            width={width}
            onRestore={() => {
              setInitialValue(currentUnsavedPageContent);
              setValue(currentUnsavedPageContent);
              removeOfflinePage(pageID);
              checkUnsavedPages();
              setopen(false);
            }}
            onCancel={() => setPreviewMode(false)}
          />
        )
        : (
          <NotPreviewMode
            unsavedPages={unsavedPages}
            slug={slug as string}
            spaceID={spaceID}
            pageID={pageID}
            onReview={() => {
              setPreviewMode(true);
            }}
            onDiscard={() => {
              removeOfflinePage(pageID);
              checkUnsavedPages();
              setopen(false);
            }}
          />
        )}
    </Popup>
  );
}

export default React.memo(UnsavedPagesPopup);
